import dayjs from 'dayjs';

/**
 * Expense Manager — recurring PrePaid journal entries loaded into Geneva.
 *
 * One expense_managers doc produces N PrePaid_InsertUpdate records in a single
 * XML via the existing generateGenevaXml bulk path. Each occurrence n has
 * KeyValue/UserTranId1 = `${docId}:${n}` and
 * ExternalSystemReferenceID = `expense_managers/${docId}:${n}`, so Geneva's
 * status write-back lands in geneva_responses.{n} on our doc, and deletes
 * (close-early) target the same keys with isUndo: true.
 *
 * Everything here is deterministic: close-early recomputes the exact same
 * occurrence dates/amounts that creation produced.
 */

export type ExpenseFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'halfYearly' | 'yearly';

export const EXPENSE_FREQUENCIES: { value: ExpenseFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'halfYearly', label: 'Half-Yearly' },
  { value: 'yearly', label: 'Yearly' },
];

export interface ExpenseManagerAudit {
  createdAt: number;
  createdBy: string;
  createdByEmail: string;
  createdByName: string;
  approvedAt?: number;
  approvedBy?: string;
  approvedByEmail?: string;
  approvedByName?: string;
}

export interface ExpenseManagerDoc {
  /** pending = created, nothing sent to Geneva until approved */
  status: 'pending' | 'active' | 'closed';
  portfolio: string;
  /** Set to the portfolio id when the portfolio is a fund (exists in the funds
   *  collection) — emitted as FundStructure on every journal entry. */
  fundStructure?: string;
  locationAccount: string;
  fromAccount: string; // debit FinAcctCode
  toAccount: string;   // credit FinAcctCode
  comments: string;
  eventDesc?: string;
  frequency: ExpenseFrequency;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  effectiveEndDate?: string; // set by close-early; occurrences after it are deleted from Geneva
  amountMode: 'total' | 'perPeriod';
  totalAmount: number;
  perPeriodAmount: number;
  occurrenceCount: number;
  audit: ExpenseManagerAudit;
  closedAt?: number;
  closedBy?: string;
  deleted?: boolean; // delete-all: every occurrence removed from Geneva
  geneva_responses?: Record<string, { status?: string; status_message?: string; code?: string }>;
}

export type ExpenseManagerRow = ExpenseManagerDoc & { id: string };

const STEP_MONTHS: Record<Exclude<ExpenseFrequency, 'daily' | 'weekly'>, number> = {
  monthly: 1,
  quarterly: 3,
  halfYearly: 6,
  yearly: 12,
};

/** All occurrence dates from start to end inclusive. Monthly+ anchors to the
 *  start day-of-month; dayjs clamps (Jan 31 + 1 month = Feb 28/29). */
export const occurrences = (start: string, end: string, frequency: ExpenseFrequency): string[] => {
  const s = dayjs(start);
  const e = dayjs(end);
  const out: string[] = [];
  for (let n = 0; ; n++) {
    const d = frequency === 'daily' ? s.add(n, 'day')
      : frequency === 'weekly' ? s.add(n, 'week')
        : s.add(n * STEP_MONTHS[frequency], 'month');
    if (d.isAfter(e)) break;
    out.push(d.format('YYYY-MM-DD'));
  }
  return out;
};

/** Split a total into n amounts at 6 dp; the last absorbs the rounding
 *  remainder so the sum equals the total exactly. Integer micro-units avoid
 *  float drift. */
export const splitAmount = (total: number, n: number): number[] => {
  if (n <= 0) return [];
  const totalMicros = Math.round(total * 1e6);
  const per = Math.floor(totalMicros / n);
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? totalMicros - per * (n - 1) : per) / 1e6);
};

/** Per-occurrence amounts for a doc, honoring the amount entry mode. */
export const occurrenceAmounts = (doc: ExpenseManagerDoc): number[] => {
  const n = occurrences(doc.startDate, doc.endDate, doc.frequency).length;
  if (doc.amountMode === 'perPeriod') {
    return Array.from({ length: n }, () => doc.perPeriodAmount);
  }
  return splitAmount(doc.totalAmount, n);
};

export const expenseKey = (docId: string, n: number) => `${docId}:${n}`;
export const expenseExternalId = (docId: string, n: number) => `expense_managers/${docId}:${n}`;

/** Rows for the generateGenevaXml bulk path — one PrePaid_InsertUpdate each. */
export const buildCreateRows = (doc: ExpenseManagerDoc, docId: string): Record<string, any>[] => {
  const dates = occurrences(doc.startDate, doc.endDate, doc.frequency);
  const amounts = occurrenceAmounts(doc);
  return dates.map((date, n) => ({
    Portfolio: doc.portfolio,
    ...(doc.fundStructure ? { FundStructure: doc.fundStructure } : {}),
    LocationAccount: doc.locationAccount,
    EventDate: `${date}T23:59:59`,
    Comments: doc.comments,
    ...(doc.eventDesc ? { EventDesc: doc.eventDesc } : {}),
    AdjLines: [
      { FinAcctCode: doc.fromAccount, DebitAmount: amounts[n], CreditAmount: 0, LineNumber: 1 },
      { FinAcctCode: doc.toAccount, DebitAmount: 0, CreditAmount: amounts[n], LineNumber: 2 },
    ],
    KeyValue: expenseKey(docId, n),
    UserTranId1: expenseKey(docId, n),
    ExternalSystemReferenceID: expenseExternalId(docId, n),
  }));
};

/** Indices of occurrences strictly after closeDate (the ones a close-early
 *  must delete from Geneva). closeDate undefined = all indices (delete-all). */
export const indicesToDelete = (doc: ExpenseManagerDoc, closeDate?: string): number[] => {
  const dates = occurrences(doc.startDate, doc.endDate, doc.frequency);
  return dates
    .map((date, n) => ({ date, n }))
    .filter(({ date }) => closeDate === undefined || date > closeDate)
    .map(({ n }) => n);
};

/** Delete rows: the undo path strips everything except these two fields. */
export const buildDeleteRows = (docId: string, indices: number[]): Record<string, any>[] =>
  indices.map((n) => ({
    KeyValue: expenseKey(docId, n),
    ExternalSystemReferenceID: expenseExternalId(docId, n),
  }));

/** Paid so far vs total, excluding occurrences deleted by close-early. */
export const paidProgress = (doc: ExpenseManagerDoc, today = dayjs().format('YYYY-MM-DD')): { paid: number; total: number } => {
  if (doc.deleted) return { paid: 0, total: 0 };
  const dates = occurrences(doc.startDate, doc.endDate, doc.frequency);
  const amounts = occurrenceAmounts(doc);
  const effectiveEnd = doc.effectiveEndDate ?? doc.endDate;
  let paid = 0;
  let total = 0;
  dates.forEach((date, n) => {
    if (date > effectiveEnd) return;
    total += Math.round(amounts[n] * 1e6);
    // Pending managers have loaded nothing into Geneva yet.
    if (date <= today && doc.status !== 'pending') paid += Math.round(amounts[n] * 1e6);
  });
  return { paid: paid / 1e6, total: total / 1e6 };
};

export const formatAmount = (value: number): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: 6 });

const CHUNK_SIZE = 200; // ponytail: one SOAP load per 200 records keeps payloads/timeouts safe

/** Synthetic bulk requests for generateGenevaXml, chunked. id: '' keeps our
 *  explicit per-row KeyValue/UserTranId1/ExternalSystemReferenceID intact. */
export const buildGenerateXmlRequests = (rows: Record<string, any>[]): Record<string, any>[] => {
  const chunks: Record<string, any>[][] = [];
  for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
    chunks.push(rows.slice(i, i + CHUNK_SIZE));
  }
  return chunks.map((chunk) => ({
    id: '',
    isBulk: true,
    orgId: 'demo',
    templateId: 'default',
    schemaType: 'geneva',
    metadata: {
      recordType: 'PrePaid_JournalEntry',
      virtualRecordId: 'PrePaid_JournalEntry',
      isMultiRecord: false,
      totalRecords: chunk.length,
      validRecords: chunk.length,
      errorRecords: 0,
    },
    bulkFormData: chunk,
  }));
};
